import { type ComponentType, useCallback, useEffect, useState } from 'react';
import type { Currency } from '@lingoda/currency';
import type { FormBag, FormProps } from '@lingoda/forms';
import { Checkbox, Form, buildValidator, withFormWrapper } from '@lingoda/forms';
import { trans } from '@lingoda/i18n';
import { Box, Button, Divider, Grid, LinearProgress, Typography } from '@lingoda/ui';
import type { SectionName } from '@lingoda/sections';
import { TermsLink } from '@lingoda/legal';
import { CouponForm } from '../CouponForm';
import { PaymentMethodType } from '../../models';
import { PaymentMethodForm } from '../PaymentMethodForm';
import { PaymentMethodDescription } from '../PaymentMethodDescription';
import { SslFooter } from '../SslFooter';
import { PaymentRequestButton } from '../PaymentRequestButton';
import { usePaymentMethodType } from '../../hooks';
import { type WithPaymentProps, withPayment } from '../../hoc';

export interface FormValues {
    paymentMethod: string;
    termsAndConditions: boolean;
    country?: string;
    name?: string;
    paymentMethodType?: PaymentMethodType;
}

export interface PaymentFormData {
    paymentMethod: string;
    termsAndConditions: boolean;
}

type OwnProps = {
    disableSubmit?: boolean;
    loading?: boolean;
    section: SectionName;
    chargeAmount: number;
    currency: Currency;
    paymentMethod: string;
    paymentSummary: string;
    canUseCoupon?: boolean;
    onCouponChange?: (coupon: string) => Promise<unknown>;
    couponCode?: string;
    isSubscription?: boolean;
};

type Props = OwnProps & WithPaymentProps & FormProps<FormValues>;

const PaymentForm = ({
    values,
    paymentSummary,
    chargeAmount,
    currency,
    setFieldValue,
    couponCode,
    onCouponChange,
    isSubscription,
    loading,
    validating,
    isValid,
    initPaymentRequest,
    section,
    updatePaymentRequest,
    submitForm,
    canUseCoupon,
    setErrorPayment,
    errorPayment,
    paymentRequest,
    disableSubmit,
}: Props) => {
    const [validPaymentMethod, setValidPaymentMethod] = useState(false);
    const [asyncValidate, setAsyncValidate] = useState<boolean>();
    const { paymentMethod, country, name } = values;
    const type = usePaymentMethodType(paymentMethod);

    useEffect(() => {
        void setFieldValue('paymentMethodType', type);
    }, [type, setFieldValue]);

    const isValidCallback = useCallback((value: boolean) => setValidPaymentMethod(value), []);

    const isLoading = loading || validating || asyncValidate;
    const inValid = !isValid || (type && !validPaymentMethod);
    const isSepa = type === PaymentMethodType.Sepa || type === PaymentMethodType.Sofort;
    const isPaymentRequest = type === PaymentMethodType.PaymentRequest;

    useEffect(() => {
        if (isPaymentRequest && paymentSummary) {
            initPaymentRequest({
                currency,
                amount: chargeAmount,
                section,
                label: paymentSummary,
            });
        }
    }, [isPaymentRequest, chargeAmount, currency, paymentSummary, initPaymentRequest, section]);

    useEffect(() => {
        updatePaymentRequest({
            currency,
            amount: chargeAmount,
            label: paymentSummary,
        });
    }, [chargeAmount, currency, paymentSummary, updatePaymentRequest]);

    const paymentRequestButtonClick = useCallback(() => {
        void submitForm();
    }, [submitForm]);

    return (
        <>
            <Form>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {canUseCoupon && onCouponChange && (
                            <Box py={2}>
                                <CouponForm
                                    couponCode={couponCode}
                                    couponDescription={undefined}
                                    onChange={onCouponChange}
                                    onAsyncValidate={setAsyncValidate}
                                />
                            </Box>
                        )}
                        {!canUseCoupon && (
                            <Box py={2}>
                                <Typography>
                                    {trans('coupon-not-available', {}, 'public-common')}
                                </Typography>
                            </Box>
                        )}
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <Box mb={2}>
                            <PaymentMethodForm
                                setError={setErrorPayment}
                                isValidCallback={isValidCallback}
                                currency={currency}
                                type={type as PaymentMethodType}
                                errorMessage={errorPayment}
                                paymentMethod={paymentMethod}
                                country={country as string}
                                name={name as string}
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Checkbox name="termsAndConditions" label={<TermsLink />} />
                    </Grid>
                    <Grid item xs={12}>
                        <Box mb={1}>
                            {isPaymentRequest && (
                                <PaymentRequestButton
                                    onClick={paymentRequestButtonClick}
                                    paymentRequest={paymentRequest}
                                    disabled={isLoading || inValid || false}
                                />
                            )}
                            {!isPaymentRequest && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    type="submit"
                                    fullWidth
                                    disabled={isLoading || inValid || disableSubmit}
                                >
                                    {isSubscription
                                        ? trans('confirm-new-course', {}, 'student-common')
                                        : trans('btn-pay-now', {}, 'public-common')}
                                </Button>
                            )}
                        </Box>
                        <Box mb={3}>{isLoading && <LinearProgress />}</Box>
                        <PaymentMethodDescription sepa={isSepa} />
                    </Grid>
                </Grid>
            </Form>
            <Box mb={3}>
                <SslFooter
                    message={
                        type
                            ? trans('ssl-encrypted', {}, 'public-common')
                            : trans('ssl-encryption-note', {}, 'public-common')
                    }
                />
            </Box>
        </>
    );
};

export const SubscriptionPaymentForm = withPayment<OwnProps>(
    withFormWrapper<Props, FormValues>({
        mapPropsToValues: ({ paymentMethod }) => ({
            termsAndConditions: false,
            paymentMethod,
            country: undefined,
        }),
        validate: (values) =>
            buildValidator({
                termsAndConditions: {
                    required: trans('form-validation-terms-and-conditions', {}, 'public-common'),
                },
            })(values),
        handleSubmit: (
            values: FormValues,
            { props: { submitPayment, section } }: FormBag<Props, FormValues>,
        ) => {
            if (values.paymentMethodType === PaymentMethodType.PaymentRequest) {
                return;
            }

            submitPayment(
                {
                    section,
                    country: values.country,
                    name: values.name,
                    paymentMethod: {
                        type: values.paymentMethodType as PaymentMethodType,
                        value: values.paymentMethod,
                    },
                },
                values,
            );
        },
    })(PaymentForm) as ComponentType<OwnProps>,
);
